import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { API_BASE_URL } from "../../../../config";
import logoImage from "../../../assets/eventrender.png";
import { Eye, EyeOff } from "lucide-react";
import { purewebContext } from "../../../../context/PurewebContext";
import { msalInstance } from "../../../../msalConfig";
import ClaritySeeThroughBackdrop from "./ClaritySeeThroughBackdrop";

const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === "production";

const LoginPageAdmin = ({
  error,
  setError,
  loginLoading,
  setLoginLoading,
  setLoginVisible,
  setIsModalOpen,
  // Show the game behind this popup in Clarity replays (see ClaritySeeThroughBackdrop).
  claritySeeThrough = false,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [backdropReady, setBackdropReady] = useState(false);
  const { setPlayfabData, setIsAdmin } = useContext(purewebContext);

  useEffect(() => {
    localStorage.setItem("userEmail", email);
  }, [email]);

  useEffect(() => {
    const inputField = document.getElementById("login_email_field_admin");
    const passwordField = document.getElementById("login_password_field_admin");
    function handleEvent(event) {
      if (event.key === "Tab") { event.preventDefault(); passwordField?.focus?.(); }
    }
    function handleEvent2(event) {
      if (event.key === "Tab") { event.preventDefault(); inputField?.focus?.(); }
    }
    inputField?.addEventListener("keydown", handleEvent);
    passwordField?.addEventListener("keydown", handleEvent2);
    return () => {
      inputField?.removeEventListener("keydown", handleEvent);
      passwordField?.removeEventListener("keydown", handleEvent2);
    };
  }, []);

  const fetchPlayfabPlayers = async ({ email, password }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/playerdetailsFrontend`,
        { email, password }
      );
      setPlayfabData(response.data);
      setIsAdmin(true);
      setLoginVisible(false);
      if (response.data?.length > 0) setIsModalOpen(true);
    } catch (e) {
      setPlayfabData([]);
      setIsAdmin(false);
      setError("Failed to login. Please check your credentials or contact support.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogin = useCallback(async () => {
    setError("");
    setLoginLoading(true);
    await fetchPlayfabPlayers({ email, password });
  }, [email, password]);

  const handleGoogleAdminLogin = useCallback(async (tokenResponse) => {
    setError("");
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/playerdetailsFrontend`, {
        idToken: tokenResponse.access_token,
      });
      setPlayfabData(response.data);
      setIsAdmin(true);
      setLoginVisible(false);
      if (response.data?.length > 0) setIsModalOpen(true);
    } catch (e) {
      setPlayfabData([]);
      setIsAdmin(false);
      setError("Failed to login. Please check your credentials or contact support.");
    } finally {
      setLoginLoading(false);
    }
  }, [setPlayfabData, setIsAdmin, setLoginVisible, setIsModalOpen, setError]);

  const handleMicrosoftAdminLogin = useCallback(async () => {
    setError("");
    setLoginLoading(true);
    try {
      const result = await msalInstance.loginPopup({ scopes: ["User.Read"] });
      const response = await axios.post(`${API_BASE_URL}/playerdetailsFrontend`, {
        msToken: result.accessToken,
      });
      setPlayfabData(response.data);
      setIsAdmin(true);
      setLoginVisible(false);
      if (response.data?.length > 0) setIsModalOpen(true);
    } catch (e) {
      if (e.errorCode !== "user_cancelled") {
        setPlayfabData([]);
        setIsAdmin(false);
        setError("Failed to login. Please check your credentials or contact support.");
      }
    } finally {
      setLoginLoading(false);
    }
  }, [setPlayfabData, setIsAdmin, setLoginVisible, setIsModalOpen, setError]);

  const triggerGoogleAdminLogin = useGoogleLogin({
    onSuccess: handleGoogleAdminLogin,
    onError: () => setError("Google sign-in failed. Please try again."),
  });

  return (
    <div
      className={`login-container il-login-overlay${
        claritySeeThrough && backdropReady ? " il-backdrop-see-through" : ""
      }`}
    >
      {claritySeeThrough && (
        <ClaritySeeThroughBackdrop color="#2a2a2a" onReady={() => setBackdropReady(true)} />
      )}
      <div className="login-box">
        <div className="login-box--title">Admin Login</div>
        <div className="login-box--subtitle">Login to see user stats.</div>
        <div style={{ display: "flex", flexDirection: "column", marginBottom: "20px", position: "relative" }}>
          <input
            id="login_email_field_admin"
            placeholder="Enter your email"
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            name="email"
          />
          <div className="password-input-wrapper">
            <input
              id="login_password_field_admin"
              placeholder="Enter your password"
              value={password}
              type={showPassword ? "text" : "password"}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              name="password"
            />
            <div className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={16} color="#6b7280" /> : <Eye size={18} color="#6b7280" />}
            </div>
          </div>
          {error && (
            <div style={{ color: "#e83232", textAlign: "left", marginTop: -15 }}>{error}</div>
          )}
        </div>
        <button onClick={handleLogin} style={{ backgroundColor: "#0AAC41" }} disabled={loginLoading}>
          {loginLoading ? "Logging in..." : "Login"}
        </button>
        {!IS_PRODUCTION && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, backgroundColor: "#444" }} />
            <span style={{ color: "#888", fontSize: 12 }}>or</span>
            <div style={{ flex: 1, height: 1, backgroundColor: "#444" }} />
          </div>
        )}
        {!IS_PRODUCTION && (
          <button
            onClick={() => triggerGoogleAdminLogin()}
            disabled={loginLoading}
            style={{ backgroundColor: "#fff", color: "#3c4043", border: "1px solid #dadce0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908C16.658 13.074 17.64 11.229 17.64 9.2z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.26A5.36 5.36 0 0 1 9 14.42c-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.892 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        )}
        {!IS_PRODUCTION && (
          <button
            onClick={handleMicrosoftAdminLogin}
            disabled={loginLoading}
            style={{ backgroundColor: "#fff", color: "#3c4043", border: "1px solid #dadce0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
              <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
              <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
              <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
            </svg>
            Continue with Microsoft
          </button>
        )}
        <button onClick={() => setLoginVisible(false)} style={{ backgroundColor: "#55d655" }}>
          Cancel
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }} className="login-box--logo">
          <div style={{ maxWidth: 250 }}>
            <img style={{ zIndex: 2 }} src={logoImage} className="logo logo-display widthofimage" alt="Logo" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPageAdmin;
